import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight, Users, Zap, Trophy, Loader2 } from 'lucide-react'
import { useLeagueMatchdaySelections } from '@/hooks/useLeagueMatchdaySelections'
import { useCurrentMatchdayFixtures } from '@/hooks/useCurrentMatchdayFixtures'
import { useAuth } from '@/context/AuthContext'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { useLeagueConfig } from '@/hooks/useLeagueConfig'
import { useSeason } from '@/context/SeasonContext'
import { isSameTeam } from '@/lib/teams'

interface LeagueSelectionsProps {
  matchday?: number
  season?: string
}

export function LeagueSelections({
  matchday: propMatchday,
  season: propSeason,
}: LeagueSelectionsProps) {
  const { user, currentMembership } = useAuth()
  const { season: contextSeason } = useSeason()
  const { competitionId } = useLeagueConfig()
  const season = propSeason || contextSeason
  const { data: currentMatchday } = useCurrentMatchday(season, competitionId)

  const [selectedMatchday, setSelectedMatchday] = useState<number | undefined>(
    propMatchday ?? currentMatchday
  )

  // Sync with propMatchday only when the prop itself changes
  useEffect(() => {
    if (propMatchday !== undefined) {
      setSelectedMatchday(propMatchday)
    }
  }, [propMatchday])

  // Initialize from currentMatchday if no prop provided (one-time)
  useEffect(() => {
    if (propMatchday === undefined && currentMatchday !== undefined && selectedMatchday === undefined) {
      setSelectedMatchday(currentMatchday)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMatchday])

  const leagueId = currentMembership?.league_id
  const { data: selections, isLoading, error } = useLeagueMatchdaySelections(
    leagueId,
    selectedMatchday,
    season
  )
  const { data: fixtures } = useCurrentMatchdayFixtures(selectedMatchday ?? 0, season, competitionId)

  // Get fixture info for a team's match
  const getFixtureForTeam = (teamName: string | null) => {
    if (!teamName || !fixtures) return null
    return fixtures.find(f =>
      isSameTeam(f.home_team, teamName) || isSameTeam(f.away_team, teamName)
    ) ?? null
  }

  const formatKickoff = (kickoffTime: string): string => {
    const date = new Date(kickoffTime)
    const now = new Date()
    const isToday = date.toDateString() === now.toDateString()
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    if (isToday) return time
    const dayStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${dayStr} ${time}`
  }

  // Count wins from both processed results and fixture scores
  const getWinResult = (selection: typeof selections extends (infer T)[] | undefined ? T : never): boolean => {
    if (!selection.has_selection || !selection.team_name) return false
    // If already processed, use the stored result
    if (selection.match_result === 'win') return true
    if (selection.processed) return false
    // Otherwise compute from fixture data
    const fixture = getFixtureForTeam(selection.team_name)
    if (!fixture) return false
    const isFinished = fixture.status === 'FT' || fixture.status === 'AET' || fixture.status === 'PEN'
    if (!isFinished || fixture.home_score == null || fixture.away_score == null) return false
    const isHome = isSameTeam(fixture.home_team, selection.team_name)
    const pickedScore = isHome ? fixture.home_score : fixture.away_score
    const opponentScore = isHome ? fixture.away_score : fixture.home_score
    return pickedScore > opponentScore
  }

  const hasVisibleSelections = selections?.some((s) => s.has_selection) ?? false

  // Upper bound: whichever is higher — currentMatchday or the prop (user may be viewing a future matchday)
  const maxMatchday = Math.max(currentMatchday ?? 1, propMatchday ?? 1, selectedMatchday ?? 1)
  const canGoPrevious = (selectedMatchday ?? 1) > 1
  const canGoNext = (selectedMatchday ?? 1) < maxMatchday
  const isCurrentMatchday = selectedMatchday !== undefined && selectedMatchday === currentMatchday

  const matchdayOptions = Array.from(
    { length: maxMatchday },
    (_, i) => i + 1
  )

  if (!leagueId) return null

  return (
    <div className="space-y-4">
      {/* Header with matchday navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-zinc-500 dark:text-zinc-400" />
          <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
            League Picks
          </h3>
          {isCurrentMatchday && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
              Current
            </span>
          )}
        </div>

        {/* Matchday Nav */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => canGoPrevious && selectedMatchday && setSelectedMatchday(selectedMatchday - 1)}
            disabled={!canGoPrevious}
            className="p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 transition-colors
                       hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <select
            value={selectedMatchday?.toString() ?? ''}
            onChange={(e) => setSelectedMatchday(parseInt(e.target.value, 10))}
            className="appearance-none bg-zinc-100 dark:bg-zinc-800 border-none rounded-lg px-3 py-1.5 text-sm font-medium text-zinc-700 dark:text-zinc-300 cursor-pointer
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          >
            {matchdayOptions.map((md) => (
              <option key={md} value={md}>
                MD {md}{md === currentMatchday ? ' (Now)' : ''}
              </option>
            ))}
          </select>

          <button
            onClick={() => canGoNext && selectedMatchday && setSelectedMatchday(selectedMatchday + 1)}
            disabled={!canGoNext}
            className="p-1.5 rounded-lg text-zinc-500 dark:text-zinc-400 transition-colors
                       hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading || selectedMatchday === undefined ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
        </div>
      ) : error ? (
        <div className="py-6 text-center text-sm text-red-500 dark:text-red-400">
          Failed to load league selections. Please try again.
        </div>
      ) : !hasVisibleSelections ? (
        <div className="py-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
          No selections have been submitted yet for Matchday {selectedMatchday}.
        </div>
      ) : (
        <>
          {/* Selections list */}
          <div className="space-y-2">
            {selections?.map((selection) => {
              const isCurrentUser = selection.user_id === user?.id
              return (
                <div
                  key={selection.user_id}
                  className={`flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${
                    isCurrentUser
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800'
                      : 'bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800'
                  }`}
                >
                  {/* Left: Name + Team */}
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Avatar initial */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-medium ${
                      isCurrentUser
                        ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300'
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                    }`}>
                      {selection.display_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                        {selection.display_name}
                        {isCurrentUser && (
                          <span className="ml-1.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                            You
                          </span>
                        )}
                      </p>
                      {selection.has_selection ? (
                        <div className="flex items-center gap-1 flex-wrap">
                          <p className="text-xs text-zinc-500 dark:text-zinc-400">
                            {selection.team_name}
                          </p>
                          <div className="flex items-center gap-1">
                            {selection.used_double_down && (
                              <span className="inline-flex items-center gap-0.5 text-violet-600 dark:text-violet-400 font-bold">
                                <Zap className="w-3 h-3" />
                                DD
                              </span>
                            )}
                            {selection.is_autopick && (
                              <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide
                                              bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                Auto
                              </span>
                            )}
                            {selection.is_forfeit && (
                              <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide
                                              bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                                Forfeit
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-zinc-400 dark:text-zinc-500 italic">
                          No selection
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Right: Result + Points */}
                  {selection.has_selection && (
                    <div className="flex items-center gap-2 shrink-0 min-w-[4rem] justify-end">
                      {selection.processed ? (
                        <>
                          <ResultBadge result={selection.match_result} />
                          <span className={`text-sm font-mono font-bold tabular-nums w-7 text-right ${
                            (selection.points_earned ?? 0) > 0
                              ? 'text-emerald-600 dark:text-emerald-400'
                              : (selection.points_earned ?? 0) < 0
                                ? 'text-red-500 dark:text-red-400'
                                : 'text-zinc-400'
                          }`}>
                            {(selection.points_earned ?? 0) > 0 ? '+' : ''}
                            {selection.points_earned ?? 0}
                          </span>
                        </>
                      ) : (
                        <FixtureStatus fixture={getFixtureForTeam(selection.team_name)} teamName={selection.team_name} formatKickoff={formatKickoff} />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Summary stats */}
          {selections && selections.length > 0 && (
            <div className="flex items-center gap-4 px-1 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {selections.filter((s) => s.has_selection).length}/{selections.length} picked
              </span>
              <span className="flex items-center gap-1">
                <Zap className="w-3.5 h-3.5 text-violet-500" />
                {selections.filter((s) => s.used_double_down).length} DD
              </span>
              <span className="flex items-center gap-1">
                <Trophy className="w-3.5 h-3.5 text-emerald-500" />
                {selections.filter((s) => getWinResult(s)).length} wins
              </span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function FixtureStatus({
  fixture,
  teamName,
  formatKickoff,
}: {
  fixture: { status: string; elapsed?: number | null; home_score?: number; away_score?: number; home_team: string; away_team: string; kickoff_time: string } | null
  teamName: string | null
  formatKickoff: (kickoffTime: string) => string
}) {
  if (!fixture) {
    return <span className="text-xs text-zinc-400 dark:text-zinc-500 font-medium">Pending</span>
  }

  const { status, elapsed, home_score, away_score } = fixture
  const hasScore = home_score != null && away_score != null

  const isLive = status === 'LIVE' || status === '1H' || status === '2H' || status === 'ET' || status === 'P'
  const isBreak = status === 'HT' || status === 'BT'
  const isFinished = status === 'FT' || status === 'AET' || status === 'PEN'
  const isDisrupted = status === 'PST' || status === 'CANC' || status === 'SUSP' || status === 'ABD'

  // Not started — show kickoff time
  if (!isLive && !isBreak && !isFinished && !isDisrupted) {
    return (
      <span className="text-xs text-zinc-400 dark:text-zinc-500 font-medium">
        {formatKickoff(fixture.kickoff_time)}
      </span>
    )
  }

  // Determine W/D/L for the picked team
  let pickResult: 'win' | 'draw' | 'loss' | null = null
  if (hasScore && teamName) {
    const isHome = isSameTeam(fixture.home_team, teamName)
    const pickedScore = isHome ? home_score! : away_score!
    const opponentScore = isHome ? away_score! : home_score!
    if (pickedScore > opponentScore) pickResult = 'win'
    else if (pickedScore < opponentScore) pickResult = 'loss'
    else pickResult = 'draw'
  }

  // Status label
  const statusLabel = isLive
    ? (elapsed ? `${elapsed}'` : status === 'P' ? 'PEN' : status)
    : isBreak
      ? status
      : isFinished
        ? status
        : status === 'CANC' ? 'CAN' : status === 'SUSP' ? 'SUS' : status

  const statusColor = isLive
    ? 'text-emerald-600 dark:text-emerald-400'
    : isBreak
      ? 'text-amber-600 dark:text-amber-400'
      : isFinished
        ? 'text-zinc-400 dark:text-zinc-500'
        : 'text-red-500 dark:text-red-400'

  return (
    <div className="flex items-center gap-1.5">
      {pickResult && isFinished && (
        <ResultBadge result={pickResult} />
      )}
      <span className={`text-[10px] font-bold ${statusColor}`}>
        {statusLabel}
      </span>
      {isLive && (
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
      )}
    </div>
  )
}

function ResultBadge({ result }: { result: 'win' | 'draw' | 'loss' | null }) {
  if (!result) return null

  const styles = {
    win: 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300',
    draw: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
    loss: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  }

  const labels = { win: 'W', draw: 'D', loss: 'L' }

  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${styles[result]}`}>
      {labels[result]}
    </span>
  )
}
